'use client';

import { FormGroup } from '@/components/ui/form-group';
import { Input } from '@/components/ui/input';
import { useAppSelector, useAppStore } from '@/lib/frontend/store/hooks';
import { useRouter } from '@/lib/frontend/hooks/useAppRouter';
import * as React from 'react';
import { PasswordInput } from './password-input';
import { Message } from '@/components/ui/message';
import { Loading } from '@/components/ui/loading';
import { Button } from '@/components/ui/button';
import { H4, P } from '@/components/ui/typography';
import SignIn from '@/lib/frontend/store/actionCreators/SignIn';
import { selectSessionUser, selectSignInPage } from '@/lib/frontend/store/helpers';
import ResetPasswordMessage from './reset-password-message';

type Props = {
  preventRedirect?: boolean;
};

// This version of the Sign In form supports prefilling via sign-in links, but
// DOES NOT support requesting new sign in links. We have disabled passwordless
// login from the backend, to avoid sending authentication emails to folks whose
// email address was used by a bot.

export function SignInForm(props: Props) {
  const user = useAppSelector(selectSessionUser);
  const signInPage = useAppSelector(selectSignInPage);

  const signedIn = user != null;
  const signInRequest = signInPage.signInRequest;

  const store = useAppStore();
  const { push } = useRouter();

  const [usingFancyLogin, setUsingFancyLogin] = React.useState(false);
  const [showSpecialResetYourPasswordError, setShowSpecialResetYourPasswordError] =
    React.useState(false);
  const [redirect, setRedirect] = React.useState<string | undefined | null>(null);
  const [form, setForm] = React.useState({
    email: '',
    password: '',
  });

  const emailInputRef = React.useRef<HTMLInputElement>(null);

  const handleInputChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm({
      ...form,
      [event.currentTarget.name]: event.currentTarget.value,
    });
  };

  const handleInputKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submit();
    }
  };

  const gotoResetPasswordPage = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (form.email.length > 0) {
      const encodedEmail = encodeURIComponent(form.email);
      push(`/account/request-password-reset?email=${encodedEmail}`);
    } else {
      push('/account/request-password-reset');
    }
  };

  const submit = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    SignIn(store, {
      email: form.email,
      password: form.password,
      preventRedirectToAccountPage: props.preventRedirect,
      onSuccessRedirectTo: redirect,
      onError: (errorResult) => {
        setShowSpecialResetYourPasswordError(
          errorResult.errorType === 'ConsiderResettingYourPasswordError',
        );
      },
    });
  };

  React.useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const tempPassword = searchParams.get('temppass');
    const email = searchParams.get('email');
    const referrer = searchParams.get('referrer');
    const redirectParam = searchParams.get('redirect');

    if (tempPassword != null && email != null) {
      setRedirect(referrer);
      setUsingFancyLogin(true);
      setForm({
        email,
        password: tempPassword,
      });
    }

    if (emailInputRef.current != null) {
      emailInputRef.current.focus();
    }

    if (redirectParam != null) {
      setRedirect(redirectParam);
    }
  }, []);

  if (signedIn) {
    return <div>Signed in</div>;
  }

  const invalidEmail = signInRequest.errorMessage != null;

  return (
    <div className="sm:max-w-[24em] flex flex-col gap-3">
      <FormGroup label="Account Email" className="m-0">
        <Input
          type="text"
          name="email"
          value={form.email}
          ref={emailInputRef}
          variant={invalidEmail ? 'destructive' : undefined}
          onChange={handleInputChanged}
          readOnly={usingFancyLogin}
          disabled={usingFancyLogin || signInRequest.pendingResponse}
          onKeyPress={handleInputKeyPress}
          className="max-w-[unset]"
        />
      </FormGroup>

      <FormGroup label={usingFancyLogin ? 'Temporary Sign-In Code' : 'Password'} className="m-0">
        <PasswordInput
          name="password"
          autoComplete="password"
          value={form.password}
          onChange={handleInputChanged}
          onKeyPress={handleInputKeyPress}
          readOnly={usingFancyLogin}
          disabled={usingFancyLogin || signInRequest.pendingResponse}
          allowRevealedPassword={true}
        />
      </FormGroup>

      {showSpecialResetYourPasswordError ? (
        <ResetPasswordMessage onClick={gotoResetPasswordPage} />
      ) : (
        signInRequest.errorMessage && (
          <Message variant="negative">{signInRequest.errorMessage}</Message>
        )
      )}

      {signInRequest.pendingResponse ? (
        <Loading message="Signing in" />
      ) : !usingFancyLogin ? (
        <Button onClick={submit}>Sign in</Button>
      ) : (
        signInRequest.errorMessage == null && (
          <Button onClick={submit}>Sign in with temporary sign-in code</Button>
        )
      )}

      {!usingFancyLogin && (
        <div className="flex flex-col gap-2 mt-3">
          <H4>Forgot your password?</H4>
          <P className="text-sm">
            <button onClick={gotoResetPasswordPage} className="underline">
              Click here
            </button>{' '}
            to reset your password.
          </P>
        </div>
      )}
    </div>
  );
}
